/**
 * The session snapshot's TIA fields on the web's side (TIA 根治 P2, T2b).
 *
 * The snapshot has two writers, which auth-core merges rather than letting
 * either replace the other (`mergeWebSessionConfig`, `writeServerSessionConfig`):
 * - the web, turn by turn: its settings, the CPU model among them as
 *   `settingsPlcModel`, and the work mode (E12); the TIA project only in the
 *   turn the user changed it (E2);
 * - the server: the CPU model it resolved, with where that came from (T1), and
 *   the project a validation job created (T8, T9).
 *
 * Which field is whose is defined once, in @realplc/types next to
 * ChatSessionConfigSnapshot (SERVER_SESSION_CONFIG_FIELDS and the lists it is
 * made of), and readServerSessionConfig reads the server's fields.
 */

import {
  readServerSessionConfig,
  SERVER_SESSION_CONFIG_FIELDS,
  type ChatSession,
  type ChatSessionConfigSnapshot,
  type GenerationConfig,
  type ServerSessionConfig,
  type TiaProjectRef,
  type TiaWorkMode,
} from '@realplc/types';
import { loadSessionTiaProjectRef } from '../services/opennessAgent';
import { useSessionTiaTargetStore } from '../stores/sessionTiaTargetStore';
import { isTiaTargetResolvedChunk } from './generationStreamState';

/**
 * A turn's config as the web sends it: what the gateway takes into the
 * session snapshot (api-gateway `createSessionConfigSnapshot`).
 *
 * The CPU model travels as the user's setting and nothing else. `plcModel` is
 * the model the server resolved (E9 ④⑤), and one the web sent would be read
 * as the setting anyway, so the type leaves no room for it.
 */
export type TurnGenerationConfig = Omit<GenerationConfig, 'plcModel'> & {
  plcModel?: never;
  /** The CPU model in the user's settings, every turn: an input to the resolution (E9 ④). */
  settingsPlcModel?: string;
  /** How the Agent validates and creates projects (E12), every turn. */
  tiaWorkMode?: TiaWorkMode;
  /**
   * The session's TIA project, only in the turn the user changed it (E2,
   * E9 ⑤): null when they cleared it, so E7's default resolves again.
   */
  tiaProjectRef?: TiaProjectRef | null;
};

function trimmed(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * The CPU model the user's settings held, as a session snapshot records it:
 * what the settings box shows again when the session is reopened.
 *
 * Never the model the server resolved. Read back into the box it would go out
 * as the setting with the next turn, and a default the server chose would
 * become the user's choice a turn later, its note gone with it (E9 ④). A
 * `plcModel` stored without a source is the setting itself, as every snapshot
 * from before P2 kept it.
 */
export function sessionSettingsPlcModel(snapshot: ChatSessionConfigSnapshot | undefined): string | undefined {
  if (!snapshot) return undefined;
  const legacySetting = snapshot.plcModelSource ? undefined : snapshot.plcModel;
  return trimmed(snapshot.settingsPlcModel)
    ?? trimmed(legacySetting)
    ?? trimmed(snapshot.targetProfile?.plcModel);
}

/**
 * The config a turn sends, and the one the browser's copy of the session
 * snapshot is written from when the turn starts (as splitClarifySubmitConfig
 * splits a card submission's).
 *
 * Both carry the work mode (E12) and the settings as they came, the CPU model
 * as `settingsPlcModel`; neither carries a `plcModel`. The project reference
 * goes in the request only, and only while the user's choice is unsent, null
 * for a clear: the server keeps the stored reference until it stores the new
 * one, and so does the browser's copy, which takes it from the turn's
 * accepted chunk (beginTurnTiaTarget). A reference a replayed config still
 * carries is dropped from both.
 */
export function splitTurnConfig(
  config: GenerationConfig,
  session: { tiaWorkMode: TiaWorkMode; unsentTiaProjectRef?: TiaProjectRef | null },
): { stored: TurnGenerationConfig; request: TurnGenerationConfig } {
  const {
    plcModel: _plcModel,
    tiaProjectRef: _replayed,
    ...rest
  } = config as GenerationConfig & { tiaProjectRef?: TiaProjectRef | null };
  const stored: TurnGenerationConfig = { ...rest, tiaWorkMode: session.tiaWorkMode };
  const request: TurnGenerationConfig = session.unsentTiaProjectRef !== undefined
    ? { ...stored, tiaProjectRef: session.unsentTiaProjectRef }
    : stored;
  return { stored, request };
}

/** Whether two readings of the server's fields (readServerSessionConfig) say the same of one of them. */
function sameServerField(a: ServerSessionConfig, b: ServerSessionConfig, field: keyof ServerSessionConfig): boolean {
  // Both readings are normalized, the reference down to its key order.
  return JSON.stringify(a[field]) === JSON.stringify(b[field]);
}

/**
 * A session snapshot with fields the server wrote, and nothing said about the
 * others: a `tia_target_resolved` chunk, or a choice a picker's request stored.
 * The resolved model comes only with its source. The same object when none of
 * it is new, so a caller can tell there is nothing to store; a session with no
 * snapshot yet has nothing to merge into.
 */
export function mergeServerTiaTarget(
  snapshot: ChatSessionConfigSnapshot | undefined,
  written: ServerSessionConfig,
): ChatSessionConfigSnapshot | undefined {
  if (!snapshot) return snapshot;
  const fields = readServerSessionConfig(written);
  const current = readServerSessionConfig(snapshot);
  const changed = SERVER_SESSION_CONFIG_FIELDS.filter((field) => (
    fields[field] !== undefined && !sameServerField(fields, current, field)
  ));
  if (changed.length === 0) return snapshot;
  return { ...snapshot, ...Object.fromEntries(changed.map((field) => [field, fields[field]])) };
}

/**
 * A session snapshot with the server's whole account of its fields, their
 * absence included: what a turn's accepted chunk carries, and what the
 * server's copy of the session holds. The web's own fields stay as the page
 * has them. The same object when the account says nothing new.
 */
export function replaceServerTiaTarget(
  snapshot: ChatSessionConfigSnapshot | undefined,
  account: ServerSessionConfig,
): ChatSessionConfigSnapshot | undefined {
  if (!snapshot) return snapshot;
  const fields = readServerSessionConfig(account);
  const current = readServerSessionConfig(snapshot);
  if (SERVER_SESSION_CONFIG_FIELDS.every((field) => sameServerField(fields, current, field))) return snapshot;
  const next: Record<string, unknown> = { ...snapshot };
  for (const field of SERVER_SESSION_CONFIG_FIELDS) delete next[field];
  // Without a source it is the setting, from before P2: the web's, like the rest.
  if (snapshot.plcModel && !snapshot.plcModelSource) next.plcModel = snapshot.plcModel;
  return { ...next, ...fields } as ChatSessionConfigSnapshot;
}

/**
 * The page's copy of a session snapshot, with the fields the server writes
 * taken from the server's copy (replaceServerTiaTarget), and the server's copy
 * whole when the page has none.
 *
 * The page opens a conversation from IndexedDB without asking the server, and
 * its copy learns of a server write only through the turn's stream. A write it
 * missed (a dropped stream, a project a validation created after the turn
 * ended) would otherwise stay missing for good wherever the server's copy of a
 * session is merged into the page's.
 */
export function adoptServerSessionConfig(
  local: ChatSessionConfigSnapshot | undefined,
  remote: ChatSessionConfigSnapshot | undefined,
): ChatSessionConfigSnapshot | undefined {
  if (!local || !remote) return local ?? remote;
  return replaceServerTiaTarget(local, remote);
}

/**
 * The page's sessions, with one session's snapshot passed through `next`; the
 * same list when that changes nothing. The list is what the page persists to
 * IndexedDB, so this is also what the next restore reads.
 */
export function sessionsWithSessionConfig(
  sessions: ChatSession[],
  sessionId: string,
  next: (snapshot: ChatSessionConfigSnapshot | undefined) => ChatSessionConfigSnapshot | undefined,
): ChatSession[] {
  let changed = false;
  const updated = sessions.map((session) => {
    if (session.id !== sessionId) return session;
    const sessionConfig = next(session.sessionConfig);
    if (sessionConfig === session.sessionConfig) return session;
    changed = true;
    return { ...session, sessionConfig };
  });
  return changed ? updated : sessions;
}

// ---------------------------------------------------------------------------
// What the server stored, taken in
// ---------------------------------------------------------------------------

/**
 * Where what the server stored for a session goes on the page: its copy of the
 * session's snapshot, which it persists to IndexedDB (AppSecure). The open
 * conversation's store follows that copy.
 */
export interface SessionSnapshotWriter {
  /**
   * Fields the server wrote, and nothing about the others (mergeServerTiaTarget).
   * With the snapshot's revision when the server gave one; false when the page
   * already took in a newer one, and nothing was written.
   */
  merge(sessionId: string, written: ServerSessionConfig, revision?: number): boolean;
  /** The server's whole account of its fields, absence included (replaceServerTiaTarget); as merge. */
  replace(sessionId: string, account: ServerSessionConfig, revision?: number): boolean;
}

/**
 * When the page last wrote what the server stored into each session's copy.
 *
 * A copy of the session fetched before such a write is older than it, however
 * late it lands: its server fields say what the server held before the turn
 * stored the user's choice, or before a chunk arrived. Adopting them would
 * put back what the write replaced, so a fetch skips them for that session
 * (T2b review), and the next fetch brings them. A turn's accepted chunk is
 * the same kind of account, taken when the gateway accepted the turn
 * (beginTurnTiaTarget).
 *
 * The server also numbers each snapshot it stores (T8: the accepted chunk's
 * `sessionTiaTargetRevision`, a project choice's `sessionConfigRevision`),
 * and every write moves the number forward. Where an account comes with one,
 * the page keeps the newer: an account older than one it took in is not
 * written at all (T5 review).
 */
export interface ServerTiaTargetWrites {
  /** Taken as a fetch of the server's copy starts, or as a turn begins. */
  mark(): number;
  /** The page wrote what the server stored into this session's copy, at `revision` when the server said it. */
  wrote(sessionId: string, revision?: number): void;
  /** Whether the page wrote into this session's copy since `mark`. */
  wroteSince(mark: number, sessionId: string): boolean;
  /** Whether an account at `revision` is older than one the page took in for this session; never for no revision. */
  isOlder(sessionId: string, revision: number | undefined): boolean;
  /** adoptServerSessionConfig, unless the page wrote into this session's copy since `mark`. */
  adopt(
    mark: number,
    sessionId: string,
    local: ChatSessionConfigSnapshot | undefined,
    remote: ChatSessionConfigSnapshot | undefined,
  ): ChatSessionConfigSnapshot | undefined;
}

export function createServerTiaTargetWrites(): ServerTiaTargetWrites {
  let clock = 0;
  const lastWrite = new Map<string, number>();
  const newestRevision = new Map<string, number>();
  const wroteSince = (mark: number, sessionId: string) => (lastWrite.get(sessionId) ?? 0) > mark;
  return {
    mark: () => clock,
    wrote: (sessionId, revision) => {
      clock += 1;
      lastWrite.set(sessionId, clock);
      if (revision !== undefined && revision > (newestRevision.get(sessionId) ?? -Infinity)) {
        newestRevision.set(sessionId, revision);
      }
    },
    wroteSince,
    isOlder: (sessionId, revision) => (
      revision !== undefined && (newestRevision.get(sessionId) ?? -Infinity) > revision
    ),
    adopt: (mark, sessionId, local, remote) => (
      wroteSince(mark, sessionId) ? local : adoptServerSessionConfig(local, remote)
    ),
  };
}

/**
 * The writer over the page's list of sessions (AppSecure hands it `sessionsRef`
 * and its `saveSessions`, which persists the list to IndexedDB). Saves only
 * when the snapshot changes, and records every write in `writes`.
 */
export function createSessionSnapshotWriter(sessions: {
  read(): ChatSession[];
  save(next: ChatSession[]): void;
  writes?: ServerTiaTargetWrites;
}): SessionSnapshotWriter {
  const write = (
    sessionId: string,
    revision: number | undefined,
    next: (snapshot: ChatSessionConfigSnapshot | undefined) => ChatSessionConfigSnapshot | undefined,
  ): boolean => {
    // An account older than one the page took in says what the server held before it.
    if (sessions.writes?.isOlder(sessionId, revision)) return false;
    // What the server just said about this session is newer than any copy of it in flight.
    sessions.writes?.wrote(sessionId, revision);
    const current = sessions.read();
    const updated = sessionsWithSessionConfig(current, sessionId, next);
    if (updated !== current) sessions.save(updated);
    return true;
  };
  return {
    merge: (sessionId, written, revision) => write(sessionId, revision, (snapshot) => mergeServerTiaTarget(snapshot, written)),
    replace: (sessionId, account, revision) => write(sessionId, revision, (snapshot) => replaceServerTiaTarget(snapshot, account)),
  };
}

/**
 * What the server stored for a session, taken in: into the page's copy of the
 * session's snapshot, and the user's choice, when it is the reference the
 * server stored, is no longer unsent. An account older than one the page
 * took in (by `revision`) is neither written nor confirms anything: the
 * choice it matches may be one the server has since replaced.
 */
export function takeServerTiaTarget(
  sessionId: string,
  stored: ServerSessionConfig,
  how: keyof SessionSnapshotWriter,
  writer: SessionSnapshotWriter,
  revision?: number,
): void {
  const fields = readServerSessionConfig(stored);
  if (!writer[how](sessionId, fields, revision)) return;
  useSessionTiaTargetStore.getState().confirmTiaProjectRefStored(sessionId, fields.tiaProjectRef);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * What the gateway stored of the session's TIA target with this turn, from the
 * turn's accepted chunk (`metadata.sessionTiaTarget`), and only when it stored
 * the turn under the session the page sent. The stream opens either way: a
 * turn whose conversation could not be stored goes on without it, and a
 * session the user may not write is stored under a new ID. Neither stored
 * what the page sent with the turn. With the snapshot's revision, when the
 * gateway gave it (`metadata.sessionTiaTargetRevision`, T8).
 */
function acceptedTiaTarget(
  chunk: unknown,
  sessionId: string,
): { account: ServerSessionConfig; revision?: number } | undefined {
  if (!isRecord(chunk) || chunk.status !== 'accepted' || !isRecord(chunk.metadata)) return undefined;
  const { sessionId: storedAs, sessionTiaTarget, sessionTiaTargetRevision } = chunk.metadata;
  if (storedAs !== sessionId || !isRecord(sessionTiaTarget)) return undefined;
  return {
    account: readServerSessionConfig(sessionTiaTarget),
    ...(typeof sessionTiaTargetRevision === 'number' && Number.isFinite(sessionTiaTargetRevision)
      ? { revision: sessionTiaTargetRevision }
      : {}),
  };
}

/** One turn's part in the session's TIA target (beginTurnTiaTarget). */
export interface TurnTiaTarget {
  /** The config the turn sends: the work mode, and the user's choice while it is unsent. */
  readonly request: TurnGenerationConfig;
  /** The config the page's copy of the snapshot is written from as the turn starts: no unsent choice in it. */
  readonly stored: TurnGenerationConfig;
  /** The first turn of a new conversation gave it `sessionId`; what was chosen for it is that session's. */
  named(sessionId: string): void;
  /** The server stored the turn under `sessionId` (AppSecure's followServerSession): its chunks are that session's. */
  renamed(sessionId: string): void;
  /**
   * Takes in the server's TIA target from one chunk of the turn's stream: what
   * the accepted chunk says the turn stored, and what a `tia_target_resolved`
   * chunk says the server wrote since. True when the chunk carried nothing else.
   */
  onChunk(chunk: unknown): boolean;
}

/**
 * A turn's part in the session's TIA target, from its send to its stream
 * (TIA 根治 P2, T2b); AppSecure's handleGenerate, which every send passes
 * through, only calls it.
 *
 * The turn sends the work mode (E12), and the user's project choice while it
 * is unsent. The choice counts as stored only when the turn's accepted chunk
 * says the gateway stored that very reference under this session. A turn that
 * failed before, a conversation the gateway could not store, a session stored
 * under another ID, and a choice made while the turn was on its way all leave
 * it dirty, and the next turn sends it. What the accepted chunk says is also
 * the page's copy of the server's fields from then on, so a write the page
 * missed comes back with the next turn.
 *
 * Unless the page wrote into that session since the turn began: then the
 * chunk, which says what the server held when it accepted the turn, is older
 * than that write (a choice the picker stored, T5 review), and it only adds
 * the fields it carries instead of replacing the server's whole account. A
 * turn that cleared the reference replaces all the same: its chunk carries no
 * reference, and only a replace takes the old one away.
 *
 * A chunk with the snapshot's revision (T8) that is older than one the page
 * took in since, a choice the picker stored with its own revision, is left
 * out altogether: it would put the older reference back (T5 review).
 */
export function beginTurnTiaTarget(input: {
  sessionId: string | null;
  config: GenerationConfig;
  tiaWorkMode: TiaWorkMode;
  writer: SessionSnapshotWriter;
  /** The same record the writer keeps of the page's writes (createSessionSnapshotWriter). */
  writes: ServerTiaTargetWrites;
}): TurnTiaTarget {
  let sessionId = input.sessionId;
  const began = input.writes.mark();
  const unsentTiaProjectRef = useSessionTiaTargetStore.getState().unsentTiaProjectRef(sessionId);
  const clearsTiaProjectRef = unsentTiaProjectRef === null;
  const { stored, request } = splitTurnConfig(input.config, {
    tiaWorkMode: input.tiaWorkMode,
    unsentTiaProjectRef,
  });
  return {
    request,
    stored,
    named(id) {
      if (sessionId !== null) return;
      sessionId = id;
      useSessionTiaTargetStore.getState().adoptDraftSession(id);
    },
    renamed(id) {
      sessionId = id;
    },
    onChunk(chunk) {
      if (!sessionId) return false;
      if (isTiaTargetResolvedChunk(chunk)) {
        takeServerTiaTarget(sessionId, chunk, 'merge', input.writer);
        return true;
      }
      const accepted = acceptedTiaTarget(chunk, sessionId);
      if (accepted) {
        const how = clearsTiaProjectRef || !input.writes.wroteSince(began, sessionId) ? 'replace' : 'merge';
        takeServerTiaTarget(sessionId, accepted.account, how, input.writer, accepted.revision);
      }
      return false;
    },
  };
}

/**
 * The event the app shell (AppSecure) listens for to take in what the server
 * stored for a session outside a turn (sessionTiaTargetStored).
 */
export const SESSION_TIA_TARGET_STORED_EVENT = 'realplc:session-tia-target-stored';

export interface SessionTiaTargetStoredDetail {
  sessionId: string;
  stored: ServerSessionConfig;
  /** The snapshot's revision the server stored it at, when it said. */
  revision?: number;
  /** `stored` is the server's whole account of its fields, absence included, not only what it wrote. */
  whole?: boolean;
}

/**
 * The server stored this for the session, outside a turn: what the project
 * picker's request stored (T8's endpoint, which answers with the server's
 * whole account and the snapshot's revision), or a reference a validation
 * wrote back (the status stream's signal). The page's copy of the session and
 * IndexedDB take it, and the user's choice, when it is the stored reference,
 * is no longer unsent, so the next turn does not send it again. The app shell
 * does both (listenForSessionTiaTargetStored).
 *
 * Without `whole` the fields are taken as written, leaving the others; with
 * it, a field the account lacks goes, as a cleared reference must. An account
 * older than one the page took in (`revision`) is left out.
 */
export function sessionTiaTargetStored(
  sessionId: string,
  stored: ServerSessionConfig,
  options: { revision?: number; whole?: boolean } = {},
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<SessionTiaTargetStoredDetail>(SESSION_TIA_TARGET_STORED_EVENT, {
    detail: {
      sessionId,
      stored: readServerSessionConfig(stored),
      ...(options.revision !== undefined ? { revision: options.revision } : {}),
      ...(options.whole ? { whole: true } : {}),
    },
  }));
}

/** Takes in every sessionTiaTargetStored through `writer`; returns the unsubscribe. */
export function listenForSessionTiaTargetStored(writer: SessionSnapshotWriter): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const onStored = (event: Event) => {
    const detail = (event as CustomEvent<SessionTiaTargetStoredDetail>).detail;
    if (detail?.sessionId) {
      takeServerTiaTarget(detail.sessionId, detail.stored, detail.whole ? 'replace' : 'merge', writer, detail.revision);
    }
  };
  window.addEventListener(SESSION_TIA_TARGET_STORED_EVENT, onStored);
  return () => window.removeEventListener(SESSION_TIA_TARGET_STORED_EVENT, onStored);
}

/**
 * A validation wrote a conversation's project reference outside a turn, a
 * project it created (E11 ③), and the status stream said so (T11). When
 * that conversation is the open one, the page reads the stored reference
 * again with the snapshot's revision (T8's picker list, `?sessionId=`) and
 * takes it in (sessionTiaTargetStored), so the picker, the readiness and the
 * next turn all see the created project. The write-back writes the reference
 * alone, and only the reference is taken in; a revision older than one the
 * page took in since is left out. Another conversation takes it in with its
 * next fetch or turn, and so does this one when the read fails. No revision
 * means the server holds no such conversation for this user.
 */
export async function reloadSessionTiaProjectRef(sessionId: string, projectId: string | null | undefined): Promise<void> {
  if (!projectId || useSessionTiaTargetStore.getState().sessionId !== sessionId) return;
  let stored: Awaited<ReturnType<typeof loadSessionTiaProjectRef>>;
  try {
    stored = await loadSessionTiaProjectRef(projectId, sessionId);
  } catch {
    return;
  }
  if (stored.sessionConfigRevision === undefined) return;
  sessionTiaTargetStored(
    sessionId,
    stored.tiaProjectRef ? { tiaProjectRef: stored.tiaProjectRef } : {},
    { revision: stored.sessionConfigRevision },
  );
}
